// src/components/generic-crud/ImportDialog.js
import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as XLSX from 'xlsx';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    Alert,
    LinearProgress,
    Box,
    List,
    ListItem,
    ListItemText,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import Iconify from 'src/components/iconify';
import axiosInstance, { endpoints } from 'src/utils/axios';
import { fDate } from 'src/utils/format-time';

export default function ImportDialog({ open, onClose, config, onSuccess }) {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        },
        maxFiles: 1,
        maxSize: 5242880, // 5MB
        onDrop: async (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                const selectedFile = acceptedFiles[0];
                setFile(selectedFile);
                setResult(null);
                setError(null);

                // Parse Excel for preview
                try {
                    const reader = new FileReader();

                    reader.onload = (e) => {
                        const data = new Uint8Array(e.target.result);
                        const workbook = XLSX.read(data, { type: 'array' });
                        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { raw: false });

                        // Show first 5 rows as preview
                        setPreviewData(jsonData.slice(0, 5));
                    };

                    reader.readAsArrayBuffer(selectedFile);
                } catch (err) {
                    console.error('Preview error:', err);
                }
            }
        },
    });

    // Helper to safely build template/import endpoint URLs.
    // If the config supplies explicit keys (templateDownload / import) use those directly.
    // Otherwise derive from the list URL for backward compatibility.
    const getEndpointUrl = (action) => {
        const apiEndpoint = config.entity.apiEndpoint;

        // Explicit endpoint keys (preferred — avoids deriving from list which may have /list in path)
        if (action === 'template/download' && apiEndpoint.templateDownload) {
            return apiEndpoint.templateDownload;
        }
        if (action === 'import' && apiEndpoint.import) {
            return apiEndpoint.import;
        }

        // Fallback: derive from list endpoint (older generic modules)
        const baseEndpoint = apiEndpoint.list;
        if (baseEndpoint.includes('?')) {
            const [path, query] = baseEndpoint.split('?');
            return `${path}/${action}?${query}`;
        }
        return `${baseEndpoint}/${action}`;
    };

    const handleDownloadTemplate = async () => {
        try {
            setDownloading(true);
            const downloadUrl = getEndpointUrl('template/download');

            const response = await axiosInstance.get(
                downloadUrl,
                { responseType: 'blob' }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${config.entity.name}-import-template.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download template error:', err);
            setError('Failed to download template. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        setUploading(true);
        setError(null);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const uploadUrl = getEndpointUrl('import');

            const response = await axiosInstance.post(
                uploadUrl,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setResult(response.data.data);

            // If successful, trigger success callback and auto-close after 2 seconds
            if (response.data.data.success > 0) {
                if (onSuccess) {
                    onSuccess();
                }

                // Auto-close and reset after 2 seconds
                setTimeout(() => {
                    handleClose();
                }, 2000);
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.response?.data?.message || 'Failed to import file. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData(null);
        setResult(null);
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="solar:import-bold" width={24} />
                    <Typography variant="h6">Import {config.entity.namePlural}</Typography>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={3}>
                    {/* Download Template Section */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Step 1: Download Template
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={downloading ? <CircularProgress size={20} /> : <Iconify icon="solar:download-bold" />}
                            onClick={handleDownloadTemplate}
                            disabled={downloading}
                            fullWidth
                        >
                            {downloading ? 'Downloading...' : 'Download Excel Template'}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Download the template, fill in your data, and upload it back.
                        </Typography>
                    </Box>

                    {/* Upload Section */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Step 2: Upload Filled Template
                        </Typography>
                        <Box
                            {...getRootProps()}
                            sx={{
                                p: 3,
                                border: '2px dashed',
                                borderColor: isDragActive ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: isDragActive ? 'action.hover' : 'background.neutral',
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: 'action.hover',
                                },
                            }}
                        >
                            <input {...getInputProps()} />
                            <Iconify
                                icon="solar:cloud-upload-bold"
                                width={48}
                                sx={{ color: 'text.secondary', mb: 1, mx: 'auto', display: 'block' }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {isDragActive
                                    ? 'Drop the file here'
                                    : 'Drag and drop file here, or click to select'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Supported formats: CSV, Excel (.xlsx, .xls) • Max size: 5MB
                            </Typography>
                        </Box>

                        {file && (
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    label={file.name}
                                    onDelete={() => {
                                        setFile(null);
                                        setPreviewData(null);
                                    }}
                                    icon={<Iconify icon="solar:file-bold" />}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Preview Section */}
                    {previewData && previewData.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Preview (First 5 rows)
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {Object.keys(previewData[0]).map((key) => (
                                                <TableCell key={key} sx={{ fontWeight: 'bold', bgcolor: 'background.neutral' }}>
                                                    {key}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewData.map((row, index) => (
                                            <TableRow key={index} hover>
                                                {Object.values(row).map((value, i) => (
                                                    <TableCell key={i}>
                                                        {value instanceof Date ? fDate(value) : String(value)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Progress */}
                    {uploading && (
                        <Box>
                            <LinearProgress />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                Uploading and processing file...
                            </Typography>
                        </Box>
                    )}

                    {/* Error */}
                    {error && <Alert severity="error">{error}</Alert>}

                    {/* Results */}
                    {result && (
                        <Box>
                            <Alert severity={result.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2">
                                    Import completed: {result.success} succeeded, {result.failed} failed
                                </Typography>
                                {result.success > 0 && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                        Modal will close automatically...
                                    </Typography>
                                )}
                            </Alert>

                            {result.errors && result.errors.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Errors:
                                    </Typography>
                                    <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                                        {result.errors.slice(0, 10).map((err, index) => (
                                            <ListItem key={index}>
                                                <ListItemText
                                                    primary={`Row ${err.row}: ${err.error}`}
                                                    secondary={err.data ? JSON.stringify(err.data) : ''}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                    {result.errors.length > 10 && (
                                        <Typography variant="caption" color="text.secondary">
                                            ... and {result.errors.length - 10} more errors
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    Close
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    startIcon={uploading ? <CircularProgress size={20} /> : <Iconify icon="solar:upload-bold" />}
                >
                    {uploading ? 'Uploading...' : 'Upload & Import'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ImportDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    config: PropTypes.object.isRequired,
    onSuccess: PropTypes.func,
};
